// Shared question metadata formatting for the platform review &
// moderation queues. Mirrors the labels used in PlatformQuestions so
// the three screens stay visually consistent (sentence-case enums,
// "Short answer" not the CSS-capitalize "Short Answer").

export function cap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

const QTYPE_LABELS: Record<string, string> = {
  mcq_single: "Multiple choice (single)",
  mcq_multi: "Multiple choice (multi)",
  true_false: "True / False",
  short_answer: "Short answer",
  long_answer: "Long answer",
  essay: "Essay",
  speaking_response: "Speaking response",
  numeric_answer: "Numeric answer",
  match_pairs: "Match pairs",
  multi_part: "Multi-part (stem only)",
};

export function typeLabel(t: string): string {
  return QTYPE_LABELS[t] ?? cap(t.replace(/_/g, " "));
}

// Level codes are display-ready already (E1–E3, L1, L2); kept as a
// helper so callers don't reach for raw codes inconsistently.
export function levelLabel(code: string): string {
  return code?.toUpperCase() ?? code;
}

// "18 Mar 2026, 14:32" — absolute, en-GB, used in title/tooltip and
// as the visible timestamp. Falls back to the raw string if unpar+able.
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Localised en-GB date+time for a candidate-facing UTC stamp. The API
// emits SQLite's bare "YYYY-MM-DD HH:MM:SS" (UTC, no timezone marker);
// new Date() would read that as LOCAL time, shifting the displayed
// hour. So we append "Z" only when the stamp lacks a timezone — a stamp
// that already carries "Z" or a "+HH:MM" offset is left untouched
// (appending a second "Z" yields "Invalid Date"). Returns the raw
// string unchanged when it can't be parsed.
export function formatStampLocal(stamp: string | null | undefined): string {
  if (!stamp) return "—";
  const hasZone = /[Zz]$/.test(stamp) || /[+-]\d{2}:?\d{2}$/.test(stamp);
  const d = new Date(hasZone ? stamp : stamp + "Z");
  if (Number.isNaN(d.getTime())) return stamp;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Compact relative age ("2 days ago") for queue freshness. Returns
// null when the timestamp is missing or unparseable so callers can
// omit the chip entirely rather than render "Invalid Date ago".
export function timeAgo(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const secs = Math.round((Date.now() - d.getTime()) / 1000);
  if (secs < 45) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} mo ago`;
  return `${Math.round(months / 12)} yr ago`;
}

// Days a queue item has been waiting — drives the "stale" highlight.
// Returns null for missing/unparseable timestamps.
export function ageInDays(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
}
